<template>
  <TaskPage class="signup-identity-page" no-back :can-continue="canSignUpRef" @action="signUp">
    <template #title>Get started</template>
    <template v-if="!loadingRef" #alert>
      <div v-if="showPreviewAlertRef" class="alert alert-warning alert-sm">
        We're in preview, and the preview is full at the moment.
        Sign up now, and we'll save your place in line.
        We're adding new people each week!
      </div>
    </template>
    <LoadingIndicator v-if="loadingRef"/>
    <template #instruction>
      <template v-if="!loadingRef">
        Please tell us your name and email address.
      </template>
    </template>
    <template v-if="!loadingRef">
      <div class="row">
        <div class="col-sm-6">
          <label for="givenNameInput" class="form-label">Given name</label>
          <input type="text" class="form-control" id="givenNameInput" ref="givenNameInputRef" :readonly="!canEditRef" v-model="signInStore.givenName"/>
        </div>
        <div class="col-sm-6">
          <label for="familyNameInput" class="form-label">Surname</label>
          <input type="text" class="form-control" id="familyNameInput" :readonly="!canEditRef" v-model="signInStore.familyName"/>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-8">
          <label for="addressInput" class="form-label">Email address</label>
          <input type="text" class="form-control" id="addressInput" ref="addressInputRef" :readonly="!canEditRef" v-model="signInStore.address"/>
        </div>
      </div>
      <VueTurnstile v-if="turnstileReadyRef" :site-key="tsKey" :appearance="captchaAppearanceRef" theme="light" v-model="captchaCodeRef" style="display: flex"/>
      <router-link class="fs-small" to="/signin">I already have an account</router-link>
    </template>
  </TaskPage>
</template>

<style lang="scss" scoped>
.alert {
  margin: 0;
}

.row {
  margin-bottom: 1rem;
}

.coupon-code input {
  width: 120px;
}
</style>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { getSafeRedirectUri, usePageTitle } from '@webapp/util/AppUtil'
import { useIdentityBlockGrantStore } from './IdentityBlockGrantStore'
import { useSignInStore } from '@webapp/auth/SignInStore'
import { useIdentityApi, SignUpRequest, BlockGrant } from '@webapp/auth/IdentityApi'
import { AddressUtil } from '@webapp/util/AddressUtil'
import { Environment } from '@webapp/util/Environment'
import TaskPage from '@webapp/util/TaskPage.vue'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import VueTurnstile from 'vue-turnstile'
import { pluralizeWithCount } from '@/util/LanguageUtil'

usePageTitle("Sign up")

const loadingRef = ref(true)
const signInRestrictedRef = ref(false)
const captchaCodeRef = ref("")
// Turnstile must render after a nextTick delay so its container element exists in the DOM.
// Without this, Cloudflare's script fires before Vue flushes the v-if block, causing
// "Invalid type for parameter container" errors that leave the captcha (and sign-up button) broken.
const turnstileReadyRef = ref(false)

const identityApi = useIdentityApi()
const signInStore = useSignInStore()
const router = useRouter()

const showPreviewAlertRef = computed(() => signInRestrictedRef.value && !signInStore.unusedGrant)
const canEditRef = computed(() => !busyRef.value)

const canSignUpRef = computed(() => !busyRef.value && !!(signInStore.givenName || signInStore.familyName) && AddressUtil.isValidEmail(signInStore.address) && !!(isDevEnv || captchaCodeRef.value))
const busyRef = ref(false)
const isDevEnv = Environment.isDevelopment()
const captchaAppearanceRef = computed(() => isDevEnv && router.currentRoute.value.query.forceCaptcha ? "always" : "interaction-only")
const tsKey = Environment.get('TURNSTILE_KEY')

const givenNameInputRef = ref<HTMLInputElement>()
const addressInputRef = ref<HTMLInputElement>()

async function signUp() {
  if (signInStore.subject && signInRestrictedRef.value) {
    // already signed up but sign in not allowed yet
    router.push('/signup/done')
  }
  else {
    try {
      busyRef.value = true

      const request = {
        givenName: signInStore.givenName,
        familyName: signInStore.familyName,
        address: signInStore.address,
        captchaCode: captchaCodeRef.value,
      } as SignUpRequest

      const newIdent = await identityApi.signUpAsync(request)
      signInStore.subject = newIdent.subject ?? ""
      // store this so the next page knows to come back here if requested
      signInStore.isNewUser = true

      await identityApi.sendSignInCodeAsync(signInStore.address)
      router.push({ path: '/signin/code', query: { redirect_uri: getSafeRedirectUri() } })
    }
    catch (error) {
      if (signInRestrictedRef.value) {
        router.push('/signup/done')
      }
    }
    finally {
      busyRef.value = false
    }
  }
}

onMounted(async () => {
  try {
    loadingRef.value = true
    signInRestrictedRef.value = await identityApi.isSignInRestrictedAsync(signInStore.subject || undefined)
  }
  finally {
    loadingRef.value = false
  }

  await nextTick()
  turnstileReadyRef.value = true

  if (signInStore.givenName) {
    addressInputRef.value!.focus()
    addressInputRef.value!.select()
  }
  else {
    givenNameInputRef.value!.focus()
  }
})

</script>
